import mixpanel from 'mixpanel-browser';

import type { AnalyticsEvent } from '@/analytics/events';

const isDebug = process.env.REACT_APP_MIXPANEL_DEBUG;

/** Hostname of the production webapp; anything else (dev site, localhost) is 'dev'. */
const PROD_HOSTNAME = 'app.simplesubwater.com';

let initialized = false;

/**
 * Initialize Mixpanel once at app startup (called from index.tsx).
 *
 * Registers super properties sent with every event:
 * - environment: 'prod' | 'dev', so reports can filter environments without URL matching
 * - app_version: the release tag baked in at build time (git describe in the deploy workflows)
 * - source: 'user', distinguishing human actions from backend-sent events
 *   (the device-lambdas Mixpanel client marks its events with $os: 'backend')
 */
export const initMixpanel = (): void => {
  if (initialized) return;
  const token = process.env.REACT_APP_MIXPANEL_TOKEN;

  if (!token) {
    console.error('Mixpanel token is missing.');
    return;
  }

  mixpanel.init(token, {
    debug: isDebug === 'true' ? true : false,
    track_pageview: false,
    persistence: 'localStorage',
  });
  mixpanel.register({
    environment: window.location.hostname === PROD_HOSTNAME ? 'prod' : 'dev',
    app_version: process.env.REACT_APP_VERSION || 'unknown',
    source: 'user',
  });
  initialized = true;
};

const useMixPanel = () => {
  const trackEvent = (eventName: AnalyticsEvent, properties: Record<string, any> = {}) => {
    // Don't track anything if Mixpanel hasn't been initialized properly
    if (!initialized || !mixpanel?.track) return;
    mixpanel.track(eventName, properties);
  };

  const setUser = (userId: string, properties: Record<string, unknown> = {}) => {
    if (!initialized || !mixpanel?.identify) return;
    mixpanel.identify(userId);
    mixpanel.people?.set?.(properties);
  };

  /** Set profile properties on the current user without re-identifying. */
  const setUserProperties = (properties: Record<string, unknown>) => {
    if (!initialized) return;
    mixpanel.people?.set?.(properties);
  };

  return { trackEvent, setUser, setUserProperties };
};

export default useMixPanel;
